import {ENTITLEMENT_KEYS} from '@packages/domains/billing/constants';
import type {EntitlementKey} from '@packages/domains/billing/types';

export {ENTITLEMENT_KEYS} from '@packages/domains/billing/constants';

/**
 * Supported features for overage tracking and auto-upgrade
 */
export type OVERAGE_SUPPORTED_FEATURE =
  | typeof ENTITLEMENT_KEYS.CDN_BANDWIDTH_LIMIT
  | typeof ENTITLEMENT_KEYS.ANALYZE_SESSION_LIMIT
  | typeof ENTITLEMENT_KEYS.OPTIMIZE_PAGE_VIEWS_LIMIT;

/**
 * Named constants for overage-supported features
 */
export const OVERAGE_SUPPORTED_FEATURE_IDS = {
  CDN_BANDWIDTH_LIMIT: ENTITLEMENT_KEYS.CDN_BANDWIDTH_LIMIT,
  ANALYZE_SESSION_LIMIT: ENTITLEMENT_KEYS.ANALYZE_SESSION_LIMIT,
  OPTIMIZE_PAGE_VIEWS_LIMIT: ENTITLEMENT_KEYS.OPTIMIZE_PAGE_VIEWS_LIMIT,
} as const;

/**
 * Array of all supported overage features (for backwards compatibility)
 */
export const OVERAGE_SUPPORTED_FEATURES: OVERAGE_SUPPORTED_FEATURE[] = [
  OVERAGE_SUPPORTED_FEATURE_IDS.CDN_BANDWIDTH_LIMIT,
  OVERAGE_SUPPORTED_FEATURE_IDS.ANALYZE_SESSION_LIMIT,
  OVERAGE_SUPPORTED_FEATURE_IDS.OPTIMIZE_PAGE_VIEWS_LIMIT,
] as const;

/**
 * Type for qualifiers used in overages configuration
 */
export type Qualifiers = EntitlementKey[];

/**
 * Overages configuration from Statsig dynamic config
 * @see {@link https://console.statsig.com/7kzGQWd4hVXvdCs5dNkqUB/dynamic_configs/dc-pymt-3073-overages-config}
 */
export type OveragesConfig = {
  anchorDate: string;
  qualifiers: Qualifiers;
  allEntitlementsDisallowedAreas?: string[];
};

/**
 * Dynamic config key for overages configuration
 */
export const OVERAGES_CONFIG_KEY = 'dc-pymt-3073-overages-config';

export type AutoUpgradeDynamicConfig = {
  autoUpgradeMultiplier?: number;
};

/**
 * Dynamic config key for auto-upgrade configuration
 */
export const AUTO_UPGRADE_CONFIG_KEY = 'dc-pymt-auto-upgrade-config';

export const SITE_PLANS_PRETTY_TO_ID: Record<string, string> = {
  starter: 'starter', // Unlike hosting-* plans, Starter uses its own slug as the plan ID
  basic: 'hosting-basic-v5',
  cms: 'hosting-cms-v5',
  business: 'hosting-business-v5',
  standard: 'hosting-ecommerce-standard-v4',
  plus: 'hosting-ecommerce-plus-v4',
  advanced: 'hosting-ecommerce-advanced-v4',

  // PnP 4.2 plans
  basic_v6: 'hosting-basic-v6',
  premium: 'hosting-premium-v1',

  // Still-sellable PNP25 plans left out when basic/cms/business moved to v5
  basic_v4: 'hosting-basic-v4',
  cms_v4: 'hosting-cms-v4',
  business_v4: 'hosting-business-v4',
  standard_v3: 'hosting-ecommerce-standard-v3',
  plus_v3: 'hosting-ecommerce-plus-v3',
  advanced_v3: 'hosting-ecommerce-advanced-v3',
};

export const WORKSPACE_PLANS_PRETTY_TO_ID: Record<string, string> = {
  freelancer: 'workspace-freelancer-seat-v1',
  core: 'workspace-core-seat-v1',
  agency: 'workspace-agency-seat-v1',
  growth: 'workspace-growth-seat-v1',
  elitePartner: 'workspace-elite-partner-seat-v1',
  enterprise: 'workspace-enterprise-v1',
};

/**
 * Stigg Site plan IDs.
 */
export const SITE_PLAN_ID_PRIMARY = {
  STARTER: 'plan-site-starter',
  BASIC: 'plan-site-basic',
  CMS: 'plan-site-cms',
  BUSINESS: 'plan-site-business',
  ECOMMERCE_STANDARD: 'plan-site-ecommerce-standard',
  ECOMMERCE_PLUS: 'plan-site-ecommerce-plus',
  ECOMMERCE_ADVANCED: 'plan-site-ecommerce-advanced',

  /** Grace Period plans */
  BASIC_GRACE_PERIOD: 'plan-site-basic-hosting-vip',
  CMS_GRACE_PERIOD: 'plan-site-cms-hosting-vip',
  BUSINESS_GRACE_PERIOD: 'plan-site-business-hosting-vip',

  /** PnP 3.0 plans */
  /**
   * If we update the latest plans please check to also update
   * the OVERAGES_QUALIFYING_PLANS constant below.
   */
  BASIC_3_0: 'plan-site-basic-3-0',
  CMS_3_0: 'plan-site-cms-3-0',
  BUSINESS_3_0: 'plan-site-business-3-0',
  ECOMMERCE_STANDARD_3_0: 'plan-site-ecommerce-standard-3-0',
  ECOMMERCE_PLUS_3_0: 'plan-site-ecommerce-plus-3-0',
  ECOMMERCE_ADVANCED_3_0: 'plan-site-ecommerce-advanced-3-0',

  /** PnP 4.2 plans */
  BASIC_4_2: 'plan-site-basic-4-2',
  PREMIUM_4_2: 'plan-site-premium-4-2',

  /** PNP 3.2 Stigg Enterprise Site Plans */
  TEAM: 'plan-site-enterprise-team',
  SCALE: 'plan-site-enterprise-scale',
  ENTERPRISE: 'plan-site-enterprise',
  ENTERPRISE_PLUS: 'plan-site-enterprise-plus',

  /** PNP 4.1 Stigg Enterprise Site Plans */
  TEAM_4_1: 'plan-site-enterprise-team-4-1',
  SCALE_4_1: 'plan-site-enterprise-scale-4-1',
  ENTERPRISE_4_1: 'plan-site-enterprise-4-1',
  ENTERPRISE_PLUS_4_1: 'plan-site-enterprise-plus-4-1',

  /** Legacy enterprise plans */
  LEGACY_ENTERPRISE_LITE: 'plan-site-legacy-enterprise-lite',
  LEGACY_ENTERPRISE_PRO: 'plan-site-legacy-enterprise-pro',
  LEGACY_ENTERPRISE: 'plan-site-legacy-enterprise-hosting',
} as const;

export const SITE_PLAN_HIGHEST_SELF_SERVE_TIERS = [
  SITE_PLAN_ID_PRIMARY.BUSINESS_3_0,
  SITE_PLAN_ID_PRIMARY.BUSINESS,
] as const;

export const OVERAGE_OCCURRENCE = {
  first_month_50: 'first_month_50',
  next_month_50: 'next_month_50',
  first_month_80: 'first_month_80',
  next_month_80: 'next_month_80',
  first_month_exceeded: 'first_month_exceeded',
  will_upgraded: 'will_upgraded',
  upgraded: 'upgraded',
} as const;

export const PRICING_MODEL = {
  FLAT_FEE: 'FLAT_FEE',
  PER_UNIT: 'PER_UNIT',
  USAGE_BASED: 'USAGE_BASED',
};

/**
 * Tax Constants
 */
export const TAX_PROVIDERS = {
  ALWAYS_TEN_DOLLARS: 'AlwaysTenDollars',
  ANROK: 'Anrok',
  AVALARA: 'Avalara',
  NO_TAX: 'NoTax',
} as const;

export const REFUND_TRANSACTION_TYPES = {
  FULL: 'Full',
  PARTIAL: 'Partial',
  TAX_ONLY: 'TaxOnly',
} as const;

export const ENTITY_USE_CODE_TYPES = {
  TAXABLE: 'taxable',
  GOV: 'gov',
  ORG: 'org',
  OTHER: 'other',
} as const;

export const TAX_EXEMPT_STATUS = {
  EXEMPT: 'exempt',
  NONE: 'none',
} as const;

export const TAX_ENTITY_ORIGIN_SOURCE = {
  COUPON: 'coupon',
  CUSTOMER: 'customer',
  ADMIN: 'admin',
} as const;

// Re-exported from billing/types (canonical source)
export {
  TAX_REJECTION_TYPE,
  TAX_VERIFICATION_STATUS,
} from '@packages/domains/billing/types';
